import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RotateCcw, Package, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { requestsService, AssetRequest } from '@/features/requests/services/requests.service';
import { Pagination } from '@/components/ui/pagination';
import { ViewModeToggle, type ViewMode } from '@/components/ui/view-mode-toggle';
import { ReturnRequestDialog } from '@/features/requests/components/ReturnRequestDialog';
import { RequestPhotos } from '@/features/requests/components/RequestPhotos';
import { formatPlannedReturn, isRequestOverdue } from '@/features/requests/lib/overdue';

export const Route = createFileRoute('/_authenticated/my-requests')({
  component: MyRequestsPage,
});

function MyRequestsPage() {
  const [returnTarget, setReturnTarget] = useState<AssetRequest | null>(null);
  const [statusFilter, setStatusFilter] = useState<AssetRequest['status'] | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  // Buscar minhas solicitações
  const { data: requestsData, isLoading } = useQuery({
    queryKey: ['my-requests', statusFilter, page, limit],
    queryFn: async () => {
      return requestsService.getMyRequests({
        status: statusFilter || undefined,
        page,
        limit,
      });
    },
  });

  const requests: AssetRequest[] = requestsData?.items || [];
  const total = requestsData?.total || 0;

  // Equipamentos comigo: retiradas aprovadas e não devolvidas, sempre visíveis
  const { data: activeData } = useQuery({
    queryKey: ['my-requests', 'active'],
    queryFn: () =>
      requestsService.getMyRequests({ status: 'approved', type: 'withdrawal', limit: 100 }),
  });
  const activeRequests = activeData?.items || [];
  const overdueRequests = activeRequests.filter((r) => isRequestOverdue(r));

  const handleReturn = (request: AssetRequest) => setReturnTarget(request);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      pending: 'secondary',
      approved: 'default',
      rejected: 'destructive',
      returned: 'outline',
    };

    const labels: Record<string, string> = {
      pending: 'Pendente',
      approved: 'Aprovada',
      rejected: 'Rejeitada',
      returned: 'Devolvida',
    };

    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Minhas Solicitações"
        description="Visualize e gerencie seus equipamentos solicitados"
      />

      {overdueRequests.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-destructive">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="space-y-1 text-sm">
            <p className="font-semibold">
              {overdueRequests.length === 1
                ? 'Você tem 1 equipamento com devolução atrasada'
                : `Você tem ${overdueRequests.length} equipamentos com devolução atrasada`}
            </p>
            <p>
              {overdueRequests
                .map(
                  (r) =>
                    `${r.asset?.name || 'Equipamento'} (previsto para ${formatPlannedReturn(r.plannedReturnDate)})`,
                )
                .join(', ')}
              . O alerta se repete todo dia no Asseto e no Mattermost até você registrar a
              devolução.
            </p>
          </div>
        </div>
      )}

      {/* Equipamentos em uso */}
      {activeRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Equipamentos comigo ({activeRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Localização/Motivo</TableHead>
                    <TableHead>Data Retirada</TableHead>
                    <TableHead>Data Retorno Prevista</TableHead>
                    <TableHead>Foto</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">{request.asset?.code || '-'}</TableCell>
                      <TableCell>{request.asset?.name || '-'}</TableCell>
                      <TableCell>{request.withdrawalLocation || '-'}</TableCell>
                      <TableCell>
                        {request.approvedAt
                          ? format(new Date(request.approvedAt), 'dd/MM/yyyy HH:mm', {
                              locale: ptBR,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {formatPlannedReturn(request.plannedReturnDate)}
                          {isRequestOverdue(request) && <Badge variant="destructive">Atrasado</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <RequestPhotos request={request} compact />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReturn(request)}
                          className="gap-2"
                        >
                          <RotateCcw className="h-4 w-4" />
                          {request.batchId ? 'Devolver Kit' : 'Devolver'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Todas as solicitações */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Histórico de Solicitações</CardTitle>
            <div className="flex items-center gap-2">
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value === 'all' ? undefined : (value as AssetRequest['status']));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="approved">Aprovada</SelectItem>
                  <SelectItem value="rejected">Rejeitada</SelectItem>
                  <SelectItem value="returned">Devolvida</SelectItem>
                </SelectContent>
              </Select>
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : !requests || requests.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Nenhuma solicitação encontrada
            </div>
          ) : (
            <>
            {viewMode === 'table' ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Localização/Motivo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data Solicitação</TableHead>
                    <TableHead>Data Retorno Prevista</TableHead>
                    <TableHead>Data Devolução</TableHead>
                    <TableHead>Fotos</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">{request.asset?.code || '-'}</TableCell>
                      <TableCell>{request.asset?.name || '-'}</TableCell>
                      <TableCell>{request.withdrawalLocation || '-'}</TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell>
                        {format(new Date(request.requestedAt), 'dd/MM/yyyy HH:mm', {
                          locale: ptBR,
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {formatPlannedReturn(request.plannedReturnDate)}
                          {isRequestOverdue(request) && <Badge variant="destructive">Atrasado</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {request.actualReturnDate
                          ? format(new Date(request.actualReturnDate), 'dd/MM/yyyy HH:mm', {
                              locale: ptBR,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <RequestPhotos request={request} compact />
                      </TableCell>
                      <TableCell className="text-right">
                        {request.status === 'approved' && request.type === 'withdrawal' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReturn(request)}
                            className="gap-2"
                          >
                            <RotateCcw className="h-4 w-4" />
                            {request.batchId ? 'Devolver Kit' : 'Devolver'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {requests.map((request) => (
                  <Card key={request.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">
                        {request.asset?.code ? `${request.asset.code} - ${request.asset.name}` : request.asset?.name || '-'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1 text-sm">
                      <p className="flex items-center gap-2">
                        <strong>Status:</strong> {getStatusBadge(request.status)}
                        {isRequestOverdue(request) && <Badge variant="destructive">Atrasado</Badge>}
                      </p>
                      <p><strong>Localização/Motivo:</strong> {request.withdrawalLocation || '-'}</p>
                      <p>
                        <strong>Solicitação:</strong>{' '}
                        {format(new Date(request.requestedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </p>
                      <p>
                        <strong>Devolução prevista:</strong>{' '}
                        {formatPlannedReturn(request.plannedReturnDate)}
                      </p>
                      <RequestPhotos request={request} compact />
                      <div className="pt-2">
                        {request.status === 'approved' && request.type === 'withdrawal' && (
                          <Button size="sm" variant="outline" onClick={() => handleReturn(request)} className="gap-2">
                            <RotateCcw className="h-4 w-4" />
                            {request.batchId ? 'Devolver Kit' : 'Devolver'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {total > limit && (
              <div className="mt-4">
                <Pagination total={total} page={page} limit={limit} onPageChange={setPage} />
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

      <ReturnRequestDialog
        open={!!returnTarget}
        onOpenChange={(open) => !open && setReturnTarget(null)}
        request={returnTarget}
      />
    </div>
  );
}
